<script setup lang="ts">
import {computed, inject, Ref, ref, watchEffect} from 'vue';
import {
  formatting,
  LegacyDelivery,
  LegacyInvoice,
  legacyOrderKey,
  LegacyPayment,
  useLocalization
} from '@hantera/portal-app';
import Decimal from 'decimal.js';
import OrderAdjustment from './OrderAdjustment.vue';
import OrderAddAdjustment from './OrderAddAdjustment.vue';
import {AdjustmentType} from '../OrderAdjustmentForm';
import {EditableOrderRule} from '../editable-order-adjustment';
import {NewRowAdjustment, newRowAdjustmentKey, orderRulesKey} from '../order-keys';
import {DecimalInputValue} from "@hantera/design-system";


const emit = defineEmits<{
  (event: 'gotoPayment', payment: LegacyPayment): void
}>();

const { currency } = formatting;
const t = useLocalization();
const order = inject(legacyOrderKey, {} as any)

const adjustments = inject(orderRulesKey, <Ref<EditableOrderRule[]>><any>[]);
const newRowAdjustment = inject(newRowAdjustmentKey, <Ref<NewRowAdjustment>><any>{});
const newFee = ref<DecimalInputValue>({
  value: new Decimal(0),
  isPercentage: false
});
const newDiscount = ref<DecimalInputValue>({
  value: new Decimal(0),
  isPercentage: false
});

const itemsValue = computed(() => {
  return order.value?.deliveries?.flatMap((delivery: LegacyDelivery) => {
    
    if (delivery.state === 'Cancelled') {
      return []
    }
    
    return delivery.rows.map((row) => {
      let qty = row.quantity.sub(row.returnedQuantity).sub(row.lostQuantity);
      if (row.quantity.gt(0) && qty.lt(0))
      {
        qty = new Decimal(0);
      }
      
      return row.unitPrice.mul(qty);
    });
  }).reduce((res: Decimal, current: Decimal) => res.add(current), new Decimal(0));
});

const fees = ref<EditableOrderRule[]>([]);
watchEffect(() => {
  fees.value = adjustments.value.filter(r => r.originalValue.gt(0));
  const shipping = order.value?.deliveries.map((r: LegacyDelivery) => {
    return r.deliveryTotal.sub(r.adjustments.reduce((res, a) => {
      return res.add(a.adjustmentTotal);
    }, new Decimal(0)));
  }).reduce((res: Decimal, d: Decimal) => res.add(d), new Decimal(0));
  fees.value.push({
    adjustmentId: '__shipping',
    editable: false,
    name: 'Shipping',
    originalValue: shipping,
    value: shipping,
    affectedRows: [],
  });
});

const discounts = ref<EditableOrderRule[]>([]);
watchEffect(() => {
  discounts.value = adjustments.value.filter(r => r.originalValue.lt(0));
});


const isEditingAdjustments = computed(() =>
  discounts.value.find(r => !r.originalValue.eq(r.value)) ||
  fees.value.find(r => !r.originalValue.eq(r.value)) ||
  !newFee.value.value.eq(0) ||
  !newDiscount.value.value.eq(0) ||
  (newRowAdjustment.value && !newRowAdjustment.value?.value.value.eq(0)));

const reservedTotal = computed(() => {
  return order.value?.payments.reduce((res: Decimal, p: LegacyPayment) => {
    return res.add(p.reservedAmount);
  }, new Decimal(0));
});
const invoicedTotal = computed(() => {
  return order.value?.invoices.reduce((res: Decimal, p: LegacyInvoice) => {
    return res.add(p.invoiceTotal);
  }, new Decimal(0));
});

const paidTotal = computed(() => {
  return order.value?.payments.reduce((res: Decimal, p: LegacyPayment) => {
    return res.add(p.paidAmount).sub(p.refundedAmount);
  }, new Decimal(0));
});
const balance = computed(() => {
  const paymentsTotal = order.value?.payments.reduce((res: Decimal, p: LegacyPayment) => {
    return res.add(p.paidAmount).sub(p.refundedAmount);
  }, new Decimal(0));
  return invoicedTotal.value.sub(paymentsTotal);
});

function gotoPayment(payment: LegacyPayment) {
  emit('gotoPayment', payment);
}

const balanceWarning = computed(() => {
  if (order.value?.state === 'Cancelled') {
    return
  }
  
  return order.value?.grandTotal.gt(invoicedTotal.value.add(reservedTotal.value));
})
</script>

<template>
  <div
    class="summary box-content box-pop p"
    v-if="order"
  >
    <div class="summary__table">
      <div class="summary__section">
        <div class="summary__row summary__subtotal">
          <div class="summary__cell">
            {{ t('orderView.summary.items') }}
          </div>
          <div class="summary__cell">
            {{ currency(itemsValue, order.currencyCode) }}
          </div>
        </div>
        <OrderAdjustment
          v-for="fee in fees"
          :key="fee.adjustmentId"
          :rule="fee"
          :type="AdjustmentType.Fee"
        />
<!--        <OrderAdjustment-->
<!--          v-if="newRowAdjustment && newRowAdjustment.value.value.gt(0)"-->
<!--          :rule="{-->
<!--            editable: false,-->
<!--            adjustmentId: '',-->
<!--            name: newRowAdjustment.name ?? '',-->
<!--            originalValue: newRowAdjustment.value.value,-->
<!--            value: newRowAdjustment.value.value,-->
<!--            affectedRows: []-->
<!--          }"-->
<!--          :type="AdjustmentType.Fee"-->
<!--        />-->

        <OrderAddAdjustment
          :type="AdjustmentType.Fee"
          v-model:value="newFee"
        />

        <OrderAdjustment
          v-for="discount in discounts"
          :key="discount.adjustmentId"
          :rule="discount"
          :type="AdjustmentType.Discount"
        />
<!--        <OrderAdjustment-->
<!--          v-if="newRowAdjustment && newRowAdjustment.value.value.lt(0)"-->
<!--          :rule="{-->
<!--            editable: false,-->
<!--            adjustmentId: '',-->
<!--            name: newRowAdjustment.name ?? '',-->
<!--            originalValue: newRowAdjustment.value,-->
<!--            value: newRowAdjustment.value,-->
<!--            affectedRows: []-->
<!--          }"-->
<!--          :type="AdjustmentType.Discount"-->
<!--        />-->

        <OrderAddAdjustment
          :type="AdjustmentType.Discount"
          v-model:value="newDiscount"
        />
      </div>

      <div class="summary__section">
        <div
          v-if="!order.includeVat"
          class="summary__row summary__vat"
          :class="{ 'summary__stale': isEditingAdjustments }"
        >
          <div class="summary__cell">
            {{ t('orderView.summary.vat') }}
          </div>
          <div class="summary__cell">
            {{ currency(order.orderVat, order.currencyCode) }}
          </div>
        </div>
        <div
          class="summary__row summary__total"
          :class="{ 'summary__stale': isEditingAdjustments }"
        >
          <div class="summary__cell">
            {{ t('orderView.summary.total') }}
          </div>
          <div class="summary__cell">
            {{ currency(order.grandTotal, order.currencyCode) }}
          </div>
        </div>
        <div
          v-if="order.includeVat"
          class="summary__row summary__vat"
          :class="{ 'summary__stale': isEditingAdjustments }"
        >
          <div class="summary__cell">
            {{ t('orderView.summary.ofVat') }}
          </div>
          <div class="summary__cell">
            {{ currency(order.orderVat, order.currencyCode) }}
          </div>
        </div>
      </div>

      <div
        v-if="!reservedTotal.eq(0)"
        class="summary__section"
        :class="{ 'summary__stale': isEditingAdjustments }"
      >
        <div class="summary__row summary__total">
          <div class="summary__cell">
            {{ t('orderView.summary.reservedSum') }}
          </div>
          <div class="summary__cell">
            {{ currency(reservedTotal, order.currencyCode) }}
          </div>
        </div>
      </div>

      <div
        v-if="!invoicedTotal.eq(0)"
        class="summary__section"
        :class="{ 'summary__stale': isEditingAdjustments }"
      >
        <div class="summary__row summary__total">
          <div class="summary__cell">
            {{ t('orderView.summary.invoicedSum') }}
          </div>
          <div class="summary__cell">
            {{ currency(invoicedTotal, order.currencyCode) }}
          </div>
        </div>
      </div>

      <div
        v-if="!paidTotal.eq(0)"
        class="summary__section"
      >
        <div class="summary__row summary__total">
          <div class="summary__cell">
            {{ t('orderView.summary.paid') }}
          </div>
          <div class="summary__cell">
            {{ currency(paidTotal, order.currencyCode) }}
          </div>
        </div>
      </div>

      <div
        v-if="balanceWarning"
        class="summary__section summary__section--warning"
        :class="{ 'summary__stale': isEditingAdjustments }"
      >
        The available balance is not enough to cover for the order total. An additional {{formatting.currency(order.grandTotal.sub(invoicedTotal).sub(reservedTotal), order.currencyCode, formatting.CurrencyDisplay.none)}} is required.
      </div>
      
    </div>
  </div>
</template>

<style>
.summary__row {
  display: grid;
  grid-template-columns: auto min-content;
}

.summary__stale {
  opacity: .3;
  filter: blur(3px);
  -webkit-filter: blur(3px);
  pointer-events: none;
}

.summary__total {
  font-weight: 600;
}

.summary__section {
  margin: 7px 0;
}

.summary__section:first-child {
  margin-top: 0;
}

.summary__section:last-child {
  margin-bottom: 0;
}

.summary__cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 10px;
}

.summary__cell:last-child {
  margin-right: 0;
}

.summary__vat {
  color: hsla(var(--color-text), .7);
}

.summary__payments {
  display: grid;
  grid-template-columns: auto min-content min-content min-content;
  row-gap: 2px;
  column-gap: 8px;
}

.summary__payments-header {
  display: contents;
  font-size: 12px;
}

.summary__payments-value {
  text-align: right;
}

.summary__section--warning {
  white-space: normal;
  background-color: var(--color-warning);
  border-radius: 4px;
  color: #000;
  padding: 5px;
}
</style>